import { TIPO_LEAD_LABELS, tipoLeadSchema, type ListLeadsQuery, type TipoLead } from '@idpe/schema';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { Badge } from '~/components/ui/Badge.js';
import {
  type SortState,
  DataTable,
  PaginationControls,
  useDataTableSort,
} from '~/components/ui/DataTable.js';
import { Field } from '~/components/ui/Field.js';
import { Select } from '~/components/ui/Select.js';
import { TIPO_TONE, leadFrom, leadSummary } from '~/features/leads/display.js';
import { useLeadsList } from '~/features/leads/hooks.js';

const searchSchema = z.object({
  tipo: tipoLeadSchema.optional(),
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1).optional(),
  perPage: z.coerce.number().int().min(1).max(100).default(20).optional(),
  sort: z.string().optional(),
});

export const Route = createFileRoute('/_auth/leads/')({
  validateSearch: (search) => searchSchema.parse(search),
  component: LeadsListPage,
});

function truncate(text: string, max = 90): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}

function LeadsListPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const [sort, setSort] = useDataTableSort(
    search.sort
      ? (() => {
          const [campo, dir] = search.sort.split(':');
          return campo
            ? ({ campo, dir: (dir === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc' } as SortState)
            : undefined;
        })()
      : undefined,
  );

  const query: Partial<ListLeadsQuery> = {
    tipo: search.tipo,
    q: search.q,
    page: search.page ?? 1,
    perPage: search.perPage ?? 20,
    sort: sort ? `${sort.campo}:${sort.dir}` : undefined,
  };

  const { data, isLoading, error, refetch } = useLeadsList(query);

  function updateSearch(patch: Partial<typeof search>) {
    navigate({
      to: '/leads',
      search: { ...search, ...patch, page: patch.page ?? 1 },
    });
  }

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ Contato</p>
        <h1 className="page-title">Mensagens recebidas</h1>
        <p className="page-sub">
          Tudo que chega pelos formulários do site: contato, sugestões de pauta e canal de
          integridade. Clique numa linha para ler a mensagem completa. Esta área é só de leitura —
          para responder, use o e-mail de quem escreveu.
        </p>
      </header>

      <section>
        <div className="toolbar">
          <div className="toolbar__row">
            <div className="toolbar__filters">
              <Field
                label="Buscar"
                placeholder="nome, e-mail, conteúdo…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') updateSearch({ q: searchInput || undefined });
                }}
                onBlur={() => updateSearch({ q: searchInput || undefined })}
              />
              <Select
                label="Tipo"
                placeholder="todos"
                value={search.tipo ?? ''}
                onChange={(e) => updateSearch({ tipo: (e.target.value as TipoLead) || undefined })}
                options={[
                  { value: '', label: 'Todos os tipos' },
                  ...Object.entries(TIPO_LEAD_LABELS).map(([v, l]) => ({ value: v, label: l })),
                ]}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: 'var(--s-5)' }}>
          <DataTable
            data={data?.data ?? []}
            loading={isLoading}
            error={
              error
                ? {
                    message: error.displayMessage,
                    retry: () => void refetch(),
                  }
                : null
            }
            keyExtractor={(l) => l.id}
            sort={sort}
            onSortChange={(s) => {
              setSort(s);
              updateSearch({ sort: s ? `${s.campo}:${s.dir}` : undefined });
            }}
            onRowClick={(l) => navigate({ to: '/leads/$id', params: { id: l.id } })}
            emptyMessage="Nenhuma mensagem recebida ainda."
            columns={[
              {
                key: 'tipo',
                header: 'Tipo',
                sortable: true,
                mobileLine: 'meta',
                cell: (l) => <Badge tone={TIPO_TONE[l.tipo]}>{TIPO_LEAD_LABELS[l.tipo]}</Badge>,
              },
              {
                key: 'de',
                header: 'De',
                mobileLine: 'primary',
                cell: (l) => <strong>{leadFrom(l)}</strong>,
              },
              {
                key: 'resumo',
                header: 'Resumo',
                mobileLine: 'secondary',
                cell: (l) => (
                  <span style={{ color: 'var(--fg-muted)' }}>{truncate(leadSummary(l))}</span>
                ),
              },
              {
                key: 'criadoEm',
                header: 'Recebido',
                sortable: true,
                mobileLine: 'meta',
                cell: (l) =>
                  new Date(l.criadoEm).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  }),
              },
            ]}
          />
        </div>

        {data && data.total > 0 && (
          <PaginationControls
            page={data.page}
            perPage={data.perPage}
            total={data.total}
            totalPages={data.totalPages}
            onPageChange={(page) => updateSearch({ page })}
          />
        )}
      </section>
    </div>
  );
}
